import React from "react";
import { cn } from "../../lib/utils";
import { type BaseComponentProps } from "../../types/puck";
import { Card } from "../ui/card";
import { FormEmbed, FormEmbedSkeleton, useSavedForm } from "../../forms/form-embed";

export interface FormBlockProps extends BaseComponentProps {
  /** Which of the business's forms to render ("contact" or a user form key). */
  formKey?: string;
  heading?: string;
  subheading?: string;
  submitButtonText?: string;
  successMessage?: string;
  backgroundColor?: string;
  /** Resolved from business metadata by form-block.config. */
  businessSlug?: string;
  hasTrackingPhone?: boolean;
  /** Editor canvas: show a placeholder instead of hiding when the form can't
   *  load (deleted key, no runtime config) so the block stays selectable. */
  /**
   * Anchor this section answers to. Defaults to "contact" because that is what every CTA and
   * the nav point at — anchored at "form", dropping this block in place of the Contact Form
   * silently broke every "Get a Quote" on the page.
   */
  anchorId?: string;
  editorHint?: boolean;
}

/**
 * Place any of the business's forms on any page — the "anywhere a form can be
 * displayed" block. Renders the saved form through the shared FormEmbed (same
 * loading skeleton, Turnstile, submit path as every other surface). A deleted
 * or unloadable form renders nothing on live sites, and a labeled placeholder
 * in the editor.
 */
export const FormBlock: React.FC<FormBlockProps> = ({
  id,
  formKey,
  heading,
  subheading,
  submitButtonText,
  successMessage,
  backgroundColor,
  businessSlug,
  hasTrackingPhone,
  anchorId,
  editorHint,
  className,
}) => {
  const { loading, data } = useSavedForm(formKey);

  let body: React.ReactNode = null;
  if (formKey && loading) {
    body = (
      <Card className="jobsite:border jobsite:border-border/80 jobsite:bg-background jobsite:p-6 jobsite:shadow-xl jobsite:sm:p-8">
        <FormEmbedSkeleton />
      </Card>
    );
  } else if (formKey && data) {
    body = (
      <Card className="jobsite:border jobsite:border-border/80 jobsite:bg-background jobsite:p-6 jobsite:shadow-xl jobsite:sm:p-8">
        <FormEmbed
          data={data}
          formKey={formKey}
          submitLabel={submitButtonText?.trim() || "Send Message"}
          successMessage={successMessage}
          businessSlug={businessSlug}
          hasTrackingPhone={hasTrackingPhone}
        />
      </Card>
    );
  } else if (editorHint) {
    body = (
      <div className="jobsite:flex jobsite:min-h-32 jobsite:items-center jobsite:justify-center jobsite:rounded-lg jobsite:border jobsite:border-dashed jobsite:border-muted-foreground/40 jobsite:p-6 jobsite:text-sm jobsite:text-muted-foreground">
        {formKey
          ? `The form "${formKey}" isn't available. Pick another form in the settings panel.`
          : "Pick a form in the settings panel."}
      </div>
    );
  }

  if (!body) {
    return null;
  }

  return (
    <section
      id={anchorId?.trim() || id || "contact"}
      className={cn("jobsite:py-16 jobsite:md:py-24 jobsite:bg-background", className)}
      style={{ backgroundColor: backgroundColor || undefined }}
    >
      <div className="jobsite:container jobsite:mx-auto jobsite:px-4">
        <div className="jobsite:mx-auto jobsite:max-w-2xl">
          {(heading || subheading) && (
            <div className="jobsite:mb-8 jobsite:text-center">
              {heading && (
                <h2 className="jobsite:font-heading jobsite:text-4xl jobsite:text-foreground jobsite:md:text-5xl">
                  {heading}
                </h2>
              )}
              {subheading && (
                <p className="jobsite:mt-4 jobsite:text-lg jobsite:text-muted-foreground jobsite:md:text-xl">
                  {subheading}
                </p>
              )}
            </div>
          )}
          {body}
        </div>
      </div>
    </section>
  );
};
